import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Truck, ShieldCheck, Sparkle, ChatCircle, ArrowsClockwise, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const { t } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then(r => setFeatured(r.data || []));
    api.get("/products", { params: { bestseller: true, limit: 8 } }).then(r => setBestsellers(r.data || []));
    api.get("/reviews", { params: { limit: 8 } }).then(r => setReviews(r.data || []));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success(t("newsletter.success")); setEmail(""); }
    catch { toast.error("Please enter a valid email"); }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-12 pt-14 lg:pt-24 pb-16 lg:pb-28 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="eyebrow text-[var(--bm-muted)]">{t("hero.eyebrow")}</div>
            <h1 className="font-serif-bm text-5xl md:text-6xl lg:text-7xl leading-[1.02] mt-5 whitespace-pre-line text-[#111]" data-testid="hero-title">{t("hero.title")}</h1>
            <p className="text-[var(--bm-ink-2)] mt-6 max-w-md text-base font-light leading-relaxed">{t("hero.sub")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-now"><span>{t("hero.cta")}</span><ArrowRight size={16} weight="thin"/></Link>
              <Link to="/shop/handbags" className="btn-secondary" data-testid="hero-cta-explore">{t("hero.cta2")}</Link>
            </div>
            <div className="mt-10 flex items-center gap-6">
              <div className="flex gap-0.5">{[0,1,2,3,4].map(i=> <Star key={i} size={14} weight="fill" className="text-[var(--bm-gold)]"/>)}</div>
              <div className="text-xs text-[var(--bm-muted)]"><span className="text-[#111] font-medium">4.9 / 5</span> · 2,800+ verified reviews</div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              <div className="zoom-img rounded-2xl overflow-hidden aspect-[3/4] bg-[#f4f4f4] col-span-1 row-span-2">
                <img src="https://images.unsplash.com/photo-1713425887916-111e58eba679?w=900&q=85&auto=format&fit=crop" alt="Boom Mode model" className="w-full h-full object-cover"/>
              </div>
              <div className="zoom-img rounded-2xl overflow-hidden aspect-square bg-[#f4f4f4]">
                <img src="https://images.unsplash.com/photo-1774702535799-4d08aec80b96?w=600&q=85&auto=format&fit=crop" alt="" className="w-full h-full object-cover"/>
              </div>
              <div className="zoom-img rounded-2xl overflow-hidden aspect-square bg-[#f4f4f4]">
                <img src="https://images.unsplash.com/photo-1713425884527-66c2e63dfb39?w=600&q=85&auto=format&fit=crop" alt="" className="w-full h-full object-cover"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-[var(--bm-bg-2)] py-16 lg:py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow">The Boom Mode promise</div>
            <h2 className="font-serif-bm text-3xl md:text-5xl mt-3">{t("why.title")}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { Icon: Truck, k: "shipping" },
              { Icon: ShieldCheck, k: "secure" },
              { Icon: Sparkle, k: "quality" },
              { Icon: ChatCircle, k: "support" },
              { Icon: ArrowsClockwise, k: "returns" },
            ].map(({ Icon, k }) => (
              <div key={k} className="card-soft p-6 text-center" data-testid={`why-${k}`}>
                <div className="w-12 h-12 rounded-full border border-[var(--bm-border)] mx-auto flex items-center justify-center mb-4">
                  <Icon size={20} weight="thin" className="text-[var(--bm-gold)]"/>
                </div>
                <div className="font-serif-bm text-lg">{t(`why.${k}`)}</div>
                <div className="text-xs text-[var(--bm-muted)] mt-1 leading-relaxed">{t(`why.${k}_d`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-16 lg:py-24" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow">Curated</div>
              <h2 className="font-serif-bm text-3xl md:text-5xl mt-3">{t("featured.title")}</h2>
              <p className="text-sm text-[var(--bm-muted)] mt-2">{t("featured.sub")}</p>
            </div>
            <Link to="/shop" className="text-sm tracking-[0.2em] uppercase border-b border-[#111] pb-1" data-testid="featured-view-all">{t("featured.view_all")} →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.slice(0,8).map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
          </div>
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="bg-black text-white py-20" data-testid="editorial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 items-center gap-12">
          <div className="zoom-img rounded-2xl overflow-hidden">
            <img src="https://images.unsplash.com/flagged/photo-1553802922-e345434156e6?w=1200&q=85" alt="" className="w-full h-[520px] object-cover"/>
          </div>
          <div>
            <div className="eyebrow text-white/60">The craft</div>
            <h3 className="font-serif-bm text-3xl md:text-5xl mt-3">Quietly made.<br/>Beautifully worn.</h3>
            <p className="text-white/70 mt-5 max-w-md font-light leading-relaxed">Each piece is hand-finished by Italian artisans using full-grain leathers and discreet gold-tone hardware. Refined essentials — without the four-figure price tag.</p>
            <Link to="/shop" className="btn-accent mt-8" data-testid="editorial-cta">Discover the collection</Link>
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="py-16 lg:py-24" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="eyebrow">Most loved</div>
            <h2 className="font-serif-bm text-3xl md:text-5xl mt-3">{t("bestsellers.title")}</h2>
            <p className="text-sm text-[var(--bm-muted)] mt-2">{t("bestsellers.sub")}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {bestsellers.slice(0,8).map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-[var(--bm-bg-2)] py-20" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow">Customer Reviews</div>
            <h2 className="font-serif-bm text-3xl md:text-5xl mt-3">{t("reviews.title")}</h2>
            <p className="text-sm text-[var(--bm-muted)] mt-2">{t("reviews.sub")}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.slice(0,6).map((r, i) => (
              <div key={r.id} className="card-soft p-6 fade-up" style={{animationDelay:`${i*80}ms`}} data-testid={`review-card-${i}`}>
                <div className="flex gap-0.5 mb-3">{Array.from({length: r.rating}).map((_,k)=> <Star key={k} size={14} weight="fill" className="text-[var(--bm-gold)]"/>)}</div>
                <div className="font-serif-bm text-lg">{r.title}</div>
                <p className="text-sm text-[var(--bm-ink-2)] mt-2 leading-relaxed">&ldquo;{r.comment}&rdquo;</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={r.avatar} alt={r.name} className="w-9 h-9 rounded-full object-cover"/>
                  <div>
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-xs text-[var(--bm-muted)]">{r.location} · Verified</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-20" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="eyebrow">Newsletter</div>
          <h2 className="font-serif-bm text-3xl md:text-5xl mt-3">{t("newsletter.title")}</h2>
          <p className="text-sm text-[var(--bm-muted)] mt-3 max-w-xl mx-auto leading-relaxed">{t("newsletter.sub")}</p>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input className="bm-input flex-1" placeholder={t("newsletter.placeholder")} value={email} onChange={e=>setEmail(e.target.value)} type="email" required data-testid="newsletter-email"/>
            <button className="btn-primary" data-testid="newsletter-submit">{t("newsletter.cta")}</button>
          </form>
        </div>
      </section>
    </>
  );
}
